import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import {
  useNotificationPreferences,
  CHANNEL_DEFAULTS,
  type NotificationChannel,
  type QuietHours,
} from "@/hooks/app/useNotificationPreferences";
import { usePushSubscription } from "@/hooks/app/usePushSubscription";

const CATEGORIES: { key: string; label: string; hint: string }[] = [
  { key: "deal", label: "Negócios", hint: "Ganho, perdido, mudança de etapa e negócios parados" },
  { key: "lead", label: "Leads", hint: "Lead atribuído a você, mudança de status" },
  { key: "activity", label: "Atividades", hint: "Reuniões próximas, lembretes e vencimentos" },
  { key: "atendimento", label: "Atendimento", hint: "Conversas sem resposta e repasses da IA" },
  { key: "coaching", label: "Feedback do gestor", hint: "Orientações gravadas pelo gestor sobre suas ligações e reuniões" },
  { key: "mention", label: "Menções", hint: "Quando você é citado" },
  { key: "system", label: "Sistema", hint: "Avisos gerais da plataforma" },
];

const CHANNELS: { key: NotificationChannel; label: string }[] = [
  { key: "in_app", label: "No app" },
  { key: "push", label: "Push" },
  { key: "email", label: "E-mail" },
  { key: "whatsapp", label: "WhatsApp" },
];

const DEFAULT_QUIET: QuietHours = { enabled: false, start: "21:00", end: "08:00", tz: "America/Sao_Paulo" };

type Matrix = Record<string, Record<NotificationChannel, boolean>>;

interface NotificationPreferencesFormProps {
  /** Chamado após salvar com sucesso (o modal usa para fechar). */
  onSaved?: () => void;
  onCancel?: () => void;
}

/**
 * Matriz categoria×canal + push do dispositivo + WhatsApp + horário de silêncio.
 * Usado embutido em Settings > Notificações e dentro do modal do sino.
 */
export function NotificationPreferencesForm({ onSaved, onCancel }: NotificationPreferencesFormProps) {
  const { preferences, isLoading, isChannelEnabled, updatePreferences, isUpdating } = useNotificationPreferences();
  const push = usePushSubscription();
  const [matrix, setMatrix] = useState<Matrix>({});
  const [quiet, setQuiet] = useState<QuietHours>(DEFAULT_QUIET);
  const [whatsappPhone, setWhatsappPhone] = useState("");

  // Hidrata o estado local UMA vez, quando as preferências chegam. Sem o guard,
  // qualquer refetch com o form aberto apagaria edições não salvas.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (isLoading || hydratedRef.current) return;
    hydratedRef.current = true;
    const next: Matrix = {};
    for (const cat of CATEGORIES) {
      next[cat.key] = {
        in_app: isChannelEnabled(cat.key, "in_app"),
        push: isChannelEnabled(cat.key, "push"),
        email: isChannelEnabled(cat.key, "email"),
        whatsapp: isChannelEnabled(cat.key, "whatsapp"),
      };
    }
    setMatrix(next);
    setQuiet(preferences?.quiet_hours ?? DEFAULT_QUIET);
    setWhatsappPhone(preferences?.whatsapp_phone ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, preferences]);

  const toggle = (cat: string, chan: NotificationChannel, value: boolean) => {
    setMatrix((prev) => ({ ...prev, [cat]: { ...prev[cat], [chan]: value } }));
  };

  const handleSave = () => {
    // Fecha (onSaved) só depois do sucesso — em erro o form continua aberto
    // com as edições preservadas.
    updatePreferences(
      { channels: matrix, quiet_hours: quiet, whatsapp_phone: whatsappPhone },
      { onSuccess: () => onSaved?.() }
    );
  };

  const whatsappEnabledSomewhere = Object.values(matrix).some((c) => c?.whatsapp);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Matriz categoria × canal */}
      <div className="overflow-x-auto">
        <div className="min-w-[480px]">
          <div className="grid grid-cols-[1fr_repeat(4,72px)] items-center gap-2 pb-2 border-b">
            <span className="text-xs font-medium text-muted-foreground">Evento</span>
            {CHANNELS.map((c) => (
              <span key={c.key} className="text-xs font-medium text-muted-foreground text-center">
                {c.label}
              </span>
            ))}
          </div>
          {CATEGORIES.map((cat) => (
            <div key={cat.key} className="grid grid-cols-[1fr_repeat(4,72px)] items-center gap-2 py-2.5 border-b last:border-0">
              <div className="min-w-0">
                <p className="text-sm font-medium leading-none">{cat.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{cat.hint}</p>
              </div>
              {CHANNELS.map((c) => (
                <div key={c.key} className="flex justify-center">
                  <Switch
                    checked={matrix[cat.key]?.[c.key] ?? CHANNEL_DEFAULTS[c.key]}
                    onCheckedChange={(v) => toggle(cat.key, c.key, v)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Push neste dispositivo */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <Label className="text-sm">Push neste dispositivo</Label>
          <p className="text-xs text-muted-foreground">
            {push.status === "unsupported"
              ? "Este navegador não suporta push."
              : push.status === "denied"
              ? "Permissão bloqueada no navegador. Libere nas configurações do site."
              : push.isSubscribed
              ? "Ativo — você recebe push mesmo com a aba fechada."
              : "Receba alertas mesmo com o Vertto fechado."}
          </p>
        </div>
        {push.isSubscribed ? (
          <Button variant="outline" size="sm" onClick={push.unsubscribe} disabled={push.isWorking}>
            {push.isWorking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Desativar
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={push.subscribe}
            disabled={push.isWorking || push.status === "unsupported" || push.status === "denied"}
          >
            {push.isWorking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Ativar push
          </Button>
        )}
      </div>

      {whatsappEnabledSomewhere && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="whatsapp_phone" className="text-sm">Seu WhatsApp</Label>
                <p className="text-xs text-muted-foreground">Para receber alertas. DDI+DDD+número (ex: 5511999999999).</p>
              </div>
              <Input
                id="whatsapp_phone"
                className="w-56"
                placeholder="5511999999999"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      <Separator />

      {/* Horário de silêncio */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Horário de silêncio</Label>
            <p className="text-xs text-muted-foreground">Suspende push, WhatsApp e os avisos no app nesse intervalo</p>
          </div>
          <Switch
            checked={quiet.enabled}
            onCheckedChange={(v) => setQuiet((q) => ({ ...q, enabled: v }))}
          />
        </div>
        {quiet.enabled && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Das</Label>
              <Input type="time" className="w-28" value={quiet.start} onChange={(e) => setQuiet((q) => ({ ...q, start: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">até</Label>
              <Input type="time" className="w-28" value={quiet.end} onChange={(e) => setQuiet((q) => ({ ...q, end: e.target.value }))} />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        )}
        <Button onClick={handleSave} disabled={isUpdating}>
          {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Salvar preferências
        </Button>
      </div>
    </div>
  );
}
